import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { SpecraButton } from '@mono/ui-components';
import { ExternalLink, RefreshCw } from 'lucide-react';
import { useState } from 'react';
import { BugStatus } from './types';
import { useBugReports } from './useBugReports';
/**
 * Get color class for bug status badge
 */ function getStatusColor(status) {
    switch(status){
        case BugStatus.OPEN:
        case BugStatus.REOPENED:
            return 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300';
        case BugStatus.IN_PROGRESS:
        case BugStatus.IN_REVIEW:
            return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300';
        case BugStatus.BLOCKED:
            return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300';
        case BugStatus.RESOLVED:
        case BugStatus.CLOSED:
            return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300';
        case BugStatus.WONT_FIX:
        case BugStatus.DUPLICATE:
        case BugStatus.CANNOT_REPRODUCE:
            return 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-300';
        default:
            return 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-300';
    }
}
/**
 * Format date to relative time or short date
 */ function formatDate(dateString) {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    if (diffDays === 0) {
        const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
        if (diffHours === 0) {
            const diffMinutes = Math.floor(diffMs / (1000 * 60));
            return diffMinutes <= 1 ? 'Just now' : `${diffMinutes}m ago`;
        }
        return diffHours === 1 ? '1h ago' : `${diffHours}h ago`;
    }
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
    });
}
export function BugReportsTable({ onRefresh }) {
    const [statusFilter, setStatusFilter] = useState(undefined);
    const { bugReports, total, loading, error, refetch } = useBugReports({
        status: statusFilter
    });
    const handleRefresh = async ()=>{
        await refetch();
        onRefresh?.();
    };
    if (loading && bugReports.length === 0) {
        return /*#__PURE__*/ _jsx("div", {
            className: "flex items-center justify-center py-12",
            children: /*#__PURE__*/ _jsxs("div", {
                className: "text-center",
                children: [
                    /*#__PURE__*/ _jsx(RefreshCw, {
                        className: "mx-auto h-8 w-8 animate-spin text-gray-400"
                    }),
                    /*#__PURE__*/ _jsx("p", {
                        className: "mt-2 text-sm text-gray-500 dark:text-gray-400",
                        children: "Loading bug reports..."
                    })
                ]
            })
        });
    }
    if (error) {
        return /*#__PURE__*/ _jsxs("div", {
            className: "rounded-lg border border-red-200 bg-red-50 p-4 dark:border-red-800 dark:bg-red-950",
            children: [
                /*#__PURE__*/ _jsxs("p", {
                    className: "text-sm text-red-800 dark:text-red-200",
                    children: [
                        "Error: ",
                        error
                    ]
                }),
                /*#__PURE__*/ _jsx(SpecraButton, {
                    onClick: handleRefresh,
                    variant: "secondary",
                    size: "sm",
                    className: "mt-2",
                    children: "Try Again"
                })
            ]
        });
    }
    if (bugReports.length === 0) {
        return /*#__PURE__*/ _jsxs("div", {
            className: "rounded-lg border border-gray-200 bg-gray-50 p-8 text-center dark:border-gray-800 dark:bg-gray-950",
            children: [
                /*#__PURE__*/ _jsx("p", {
                    className: "text-sm text-gray-500 dark:text-gray-400",
                    children: "No bug reports yet."
                }),
                /*#__PURE__*/ _jsx("p", {
                    className: "mt-1 text-xs text-gray-400 dark:text-gray-500",
                    children: "When you report a bug, it will appear here."
                })
            ]
        });
    }
    return /*#__PURE__*/ _jsxs("div", {
        className: "space-y-4",
        children: [
            /*#__PURE__*/ _jsxs("div", {
                className: "flex items-center justify-between",
                children: [
                    /*#__PURE__*/ _jsxs("div", {
                        className: "flex items-center gap-2",
                        children: [
                            /*#__PURE__*/ _jsx("h3", {
                                className: "text-lg font-semibold",
                                children: "My Bug Reports"
                            }),
                            /*#__PURE__*/ _jsx("span", {
                                className: "rounded-full bg-gray-100 px-2.5 py-0.5 text-xs font-medium text-gray-600 dark:bg-gray-800 dark:text-gray-400",
                                children: total
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsxs("div", {
                        className: "flex items-center gap-2",
                        children: [
                            /*#__PURE__*/ _jsxs("select", {
                                value: statusFilter || '',
                                onChange: (e)=>setStatusFilter(e.target.value ? e.target.value : undefined),
                                className: "rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm dark:border-gray-700 dark:bg-gray-900",
                                children: [
                                    /*#__PURE__*/ _jsx("option", {
                                        value: "",
                                        children: "All Statuses"
                                    }),
                                    Object.values(BugStatus).map((status)=>/*#__PURE__*/ _jsx("option", {
                                            value: status,
                                            children: status
                                        }, status))
                                ]
                            }),
                            /*#__PURE__*/ _jsxs(SpecraButton, {
                                onClick: handleRefresh,
                                variant: "secondary",
                                size: "sm",
                                disabled: loading,
                                className: "flex items-center gap-1",
                                children: [
                                    /*#__PURE__*/ _jsx(RefreshCw, {
                                        className: `h-4 w-4 ${loading ? 'animate-spin' : ''}`
                                    }),
                                    "Refresh"
                                ]
                            })
                        ]
                    })
                ]
            }),
            /*#__PURE__*/ _jsx("div", {
                className: "overflow-hidden rounded-lg border border-gray-200 dark:border-gray-800",
                children: /*#__PURE__*/ _jsx("div", {
                    className: "overflow-x-auto",
                    children: /*#__PURE__*/ _jsxs("table", {
                        className: "min-w-full divide-y divide-gray-200 dark:divide-gray-800",
                        children: [
                            /*#__PURE__*/ _jsx("thead", {
                                className: "bg-gray-50 dark:bg-gray-900",
                                children: /*#__PURE__*/ _jsxs("tr", {
                                    children: [
                                        /*#__PURE__*/ _jsx("th", {
                                            scope: "col",
                                            className: "px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400",
                                            children: "Ticket"
                                        }),
                                        /*#__PURE__*/ _jsx("th", {
                                            scope: "col",
                                            className: "px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400",
                                            children: "Title"
                                        }),
                                        /*#__PURE__*/ _jsx("th", {
                                            scope: "col",
                                            className: "px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400",
                                            children: "Status"
                                        }),
                                        /*#__PURE__*/ _jsx("th", {
                                            scope: "col",
                                            className: "px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400",
                                            children: "Assignee"
                                        }),
                                        /*#__PURE__*/ _jsx("th", {
                                            scope: "col",
                                            className: "px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400",
                                            children: "Created"
                                        }),
                                        /*#__PURE__*/ _jsx("th", {
                                            scope: "col",
                                            className: "relative px-4 py-3",
                                            children: /*#__PURE__*/ _jsx("span", {
                                                className: "sr-only",
                                                children: "Actions"
                                            })
                                        })
                                    ]
                                })
                            }),
                            /*#__PURE__*/ _jsx("tbody", {
                                className: "divide-y divide-gray-200 bg-white dark:divide-gray-800 dark:bg-gray-950",
                                children: bugReports.map((bug)=>/*#__PURE__*/ _jsxs("tr", {
                                        className: "transition-colors hover:bg-gray-50 dark:hover:bg-gray-900",
                                        children: [
                                            /*#__PURE__*/ _jsx("td", {
                                                className: "whitespace-nowrap px-4 py-3",
                                                children: /*#__PURE__*/ _jsx("span", {
                                                    className: "font-mono text-sm font-medium text-gray-900 dark:text-gray-100",
                                                    children: bug.jiraTicketNumber
                                                })
                                            }),
                                            /*#__PURE__*/ _jsx("td", {
                                                className: "px-4 py-3",
                                                children: /*#__PURE__*/ _jsxs("div", {
                                                    className: "max-w-md",
                                                    children: [
                                                        /*#__PURE__*/ _jsx("p", {
                                                            className: "truncate text-sm font-medium text-gray-900 dark:text-gray-100",
                                                            children: bug.title
                                                        }),
                                                        /*#__PURE__*/ _jsx("p", {
                                                            className: "truncate text-xs text-gray-500 dark:text-gray-400",
                                                            children: bug.description
                                                        })
                                                    ]
                                                })
                                            }),
                                            /*#__PURE__*/ _jsx("td", {
                                                className: "whitespace-nowrap px-4 py-3",
                                                children: /*#__PURE__*/ _jsx("span", {
                                                    className: `inline-flex rounded-full px-2.5 py-0.5 text-xs font-medium ${getStatusColor(bug.status)}`,
                                                    children: bug.status
                                                })
                                            }),
                                            /*#__PURE__*/ _jsx("td", {
                                                className: "whitespace-nowrap px-4 py-3",
                                                children: bug.assignee ? /*#__PURE__*/ _jsx("span", {
                                                    className: "text-sm text-gray-900 dark:text-gray-100",
                                                    children: bug.assignee
                                                }) : /*#__PURE__*/ _jsx("span", {
                                                    className: "text-sm italic text-gray-400 dark:text-gray-600",
                                                    children: "Unassigned"
                                                })
                                            }),
                                            /*#__PURE__*/ _jsx("td", {
                                                className: "whitespace-nowrap px-4 py-3 text-sm text-gray-500 dark:text-gray-400",
                                                children: formatDate(bug.createdAt)
                                            }),
                                            /*#__PURE__*/ _jsx("td", {
                                                className: "whitespace-nowrap px-4 py-3 text-right text-sm",
                                                children: /*#__PURE__*/ _jsxs("a", {
                                                    href: bug.jiraUrl,
                                                    target: "_blank",
                                                    rel: "noopener noreferrer",
                                                    className: "inline-flex items-center gap-1 text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300",
                                                    children: [
                                                        "View",
                                                        /*#__PURE__*/ _jsx(ExternalLink, {
                                                            className: "h-3.5 w-3.5"
                                                        })
                                                    ]
                                                })
                                            })
                                        ]
                                    }, bug.bugReportID))
                            })
                        ]
                    })
                })
            }),
            /*#__PURE__*/ _jsxs("div", {
                className: "text-center text-sm text-gray-500 dark:text-gray-400",
                children: [
                    "Showing ",
                    bugReports.length,
                    " of ",
                    total,
                    " bug reports"
                ]
            })
        ]
    });
}
